import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Frontend leg of the Meta master OAuth dance.
 *
 * Meta redirects the admin to this page with `?code=…&state=…` (or
 * `?error=access_denied&error_reason=…` on cancel). We POST the trio
 * `{code, state, redirect_uri}` to the backend so it can exchange the
 * code for a long-lived token, then bounce back to the integration page.
 *
 * The redirect_uri MUST byte-for-byte match what we sent in
 * `MetaIntegrationsPage.startConnect` — `${origin}/integrations/meta/callback`.
 */
export default function MetaOAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("working"); // working | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const ran = useRef(false);  // strict-mode guards against double-POST

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error") || params.get("error_reason");
    const errDesc = params.get("error_description");

    // User clicked "Not now" in the Meta dialog
    if (err) {
      const detail = errDesc || err;
      logger.warn("Meta OAuth cancelled", { err, errDesc });
      navigate(`/integrations/meta?error=${encodeURIComponent(err)}` +
               `&detail=${encodeURIComponent(detail)}`,
               { replace: true });
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setErrorMsg("Meta did not return a code or state in the URL.");
      return;
    }

    const redirect_uri = `${window.location.origin}/integrations/meta/callback`;
    (async () => {
      try {
        // Route the callback based on the state prefix:
        //   mms_  -> master OAuth (came from /integrations/meta)
        //   mts_  -> per-client OAuth (came from /clients/<id>/meta-ads)
        const isPerClient = state.startsWith("mts_");
        if (isPerClient) {
          const cid = sessionStorage.getItem("meta_oauth_client_id");
          if (!cid) {
            throw new Error(
              "Per-client OAuth completed but no client_id was stashed in the " +
              "browser. Re-start the connect flow from the client's Meta Ads page.",
            );
          }
          await api.post(`/clients/${cid}/meta/oauth/callback`,
            { code, state, redirect_uri });
          setStatus("success");
          sessionStorage.removeItem("meta_oauth_client_id");
          setTimeout(() => navigate(`/clients/${cid}/meta-ads?connected=1`,
                                    { replace: true }), 600);
          return;
        }
        await api.post("/integrations/meta/oauth/callback",
          { code, state, redirect_uri });
        setStatus("success");
        // Quick celebratory pause then redirect so the admin sees the green tick
        setTimeout(() => navigate("/integrations/meta?connected=1",
                                  { replace: true }), 600);
      } catch (e) {
        const detail = e.response?.data?.detail || e.message || "Unknown error";
        logger.error("Meta OAuth callback exchange failed", detail);
        setStatus("error");
        setErrorMsg(detail);
        // Give the admin time to read the message — they can click "Back".
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="meta-oauth-callback">
      <div className="max-w-md w-full text-center space-y-4 p-8">
        {status === "working" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-[#1877F2] animate-spin" />
            <div className="font-display font-bold text-xl">Finalising Meta connection…</div>
            <p className="text-sm text-zinc-500">
              Exchanging the authorisation code for a long-lived access token.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <div className="font-display font-bold text-xl">Connected!</div>
            <p className="text-sm text-zinc-500">Redirecting you back…</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="h-10 w-10 mx-auto text-red-600" />
            <div className="font-display font-bold text-xl">Connection failed</div>
            <p className="text-sm text-red-700 font-mono break-words">{errorMsg}</p>
            <button
              type="button"
              onClick={() => navigate("/integrations/meta", { replace: true })}
              className="mt-4 inline-flex items-center rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white text-sm px-4 h-9"
              data-testid="meta-oauth-back">
              Back to Meta integration
            </button>
          </>
        )}
      </div>
    </div>
  );
}
